import { useState } from 'preact/hooks';
import { api } from '../services/api';

interface Child {
  id: string;
  firstName: string;
  lastName: string;
  birthdate: string;
  allergies?: string;
  medicalNotes?: string;
  family: {
    id: string;
    parentName: string;
  };
}

interface ManualCheckinModalProps {
  children: Child[];
  onClose: () => void;
  onSuccess: () => void;
}

export function ManualCheckinModal({ children, onClose, onSuccess }: ManualCheckinModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedChild, setSelectedChild] = useState<Child | null>(null);
  const [checkingIn, setCheckingIn] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [assignedCode, setAssignedCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsSent, setSmsSent] = useState(false);
  const [smsError, setSmsError] = useState<string | null>(null);

  // Calculate age from birthdate
  const getChildAge = (birthdate: string): number => {
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Filter children based on search term
  const filteredChildren = children.filter(child => {
    const fullName = `${child.firstName} ${child.lastName}`.toLowerCase();
    const familyName = child.family.parentName.toLowerCase();
    const search = searchTerm.toLowerCase();
    return fullName.includes(search) || familyName.includes(search);
  });

  const handleSelectChild = (child: Child) => {
    setSelectedChild(child);
    setError('');
  };

  const handleCheckin = async () => {
    if (!selectedChild) return;

    setCheckingIn(true);
    setError('');

    try {
      const result = await api.manualCheckin(selectedChild.id, phoneNumber || undefined);

      if (result.success) {
        setSuccess(true);
        setAssignedCode(result.checkin.code);
        setSmsSent(!!(result as any).checkin?.smsSent);
        setSmsError((result as any).checkin?.smsError || null);
        onSuccess();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to check in child');
    } finally {
      setCheckingIn(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(assignedCode);
  };

  const handleBackdropClick = (e: MouseEvent) => {
    if ((e.target as HTMLElement).classList.contains('modal-backdrop')) {
      onClose();
    }
  };

  return (
    <div 
      className="modal-backdrop fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
      onClick={handleBackdropClick}
    >
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Modal Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Manual Check-In</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Success State - Show Code */}
          {success && assignedCode && (
            <div className="text-center py-8">
              <div className="mb-4">
                <svg className="w-16 h-16 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">
                {selectedChild?.firstName} {selectedChild?.lastName} Checked In!
              </h4>
              <p className="text-gray-600 mb-6">Pickup Code:</p>
              <div className="bg-primary-50 border-2 border-primary-500 rounded-lg p-6 mb-4">
                <div className="text-6xl font-bold text-primary-600 tracking-wider">
                  {assignedCode}
                </div>
              </div>
              {smsSent && phoneNumber && (
                <p className="text-sm text-green-600 mb-3">
                  ✓ Code sent via SMS to {phoneNumber}
                </p>
              )}
              {!smsSent && phoneNumber && (
                <div className="flex items-start bg-yellow-50 border border-yellow-300 rounded-md px-4 py-3 mb-3 text-left">
                  <svg className="w-5 h-5 text-yellow-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-yellow-800">SMS not delivered to {phoneNumber}</p>
                    {smsError && (
                      <p className="text-xs text-yellow-700 mt-0.5">{smsError}</p>
                    )}
                    <p className="text-xs text-yellow-700 mt-0.5">Please share the code manually.</p>
                  </div>
                </div>
              )}
              <button
                onClick={copyCode}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy Code
              </button>
              <p className="text-sm text-gray-500 mt-4">
                <button
                  onClick={onClose}
                  className="text-primary-600 hover:text-primary-800 underline"
                >
                  Close
                </button>
              </p>
            </div>
          )}

          {/* Main Form - Search and Select */}
          {!success && (
            <>
              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex">
                    <svg className="w-5 h-5 text-red-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Search Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search for a child
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by child name or family name..."
                    className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    value={searchTerm}
                    onInput={(e) => setSearchTerm((e.target as HTMLInputElement).value)}
                    autoFocus
                  />
                  <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>

              {/* Child Selection List — hidden once a child is selected */}
              {!selectedChild && (
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg mb-4">
                  {filteredChildren.length === 0 && (
                    <div className="p-8 text-center text-gray-500">
                      {searchTerm ? 'No children found matching your search' : 'Start typing to search for a child'}
                    </div>
                  )}
                  {filteredChildren.map(child => (
                    <button
                      key={child.id}
                      onClick={() => handleSelectChild(child)}
                      className="w-full text-left p-4 border-b border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">
                            {child.firstName} {child.lastName}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            Family: {child.family.parentName} • Age: {getChildAge(child.birthdate)}
                          </div>
                          {(child.allergies || child.medicalNotes) && (
                            <div className="mt-2 space-y-1">
                              {child.allergies && (
                                <div className="flex items-center text-xs text-orange-600">
                                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                  </svg>
                                  Allergies: {child.allergies}
                                </div>
                              )}
                              {child.medicalNotes && (
                                <div className="flex items-center text-xs text-red-600">
                                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                  </svg>
                                  Medical: {child.medicalNotes}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Confirmation panel — shown after a child is selected */}
              {selectedChild && (
                <div className="mb-4 border border-primary-200 rounded-lg bg-primary-50 p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-sm text-gray-500 mb-0.5">Checking in</p>
                      <p className="font-semibold text-gray-900">
                        {selectedChild.firstName} {selectedChild.lastName}
                      </p>
                      <p className="text-sm text-gray-600">
                        Family: {selectedChild.family.parentName} • Age: {getChildAge(selectedChild.birthdate)}
                      </p>
                      {selectedChild.allergies && (
                        <p className="text-xs text-orange-600 mt-1">⚠ Allergies: {selectedChild.allergies}</p>
                      )}
                      {selectedChild.medicalNotes && (
                        <p className="text-xs text-red-600 mt-0.5">⚠ Medical: {selectedChild.medicalNotes}</p>
                      )}
                    </div>
                    <button
                      onClick={() => { setSelectedChild(null); setPhoneNumber(''); }}
                      className="text-xs text-primary-600 hover:text-primary-800 underline ml-4 whitespace-nowrap"
                    >
                      Change
                    </button>
                  </div>

                  {/* Phone Number Input — always visible in the confirmation panel */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Parent Phone Number
                      <span className="text-gray-400 font-normal ml-1">(optional – sends pickup code via SMS)</span>
                    </label>
                    <input
                      type="tel"
                      placeholder="e.g. 0712 345 678"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      value={phoneNumber}
                      onInput={(e) => setPhoneNumber((e.target as HTMLInputElement).value)}
                    />
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  disabled={checkingIn}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCheckin}
                  disabled={!selectedChild || checkingIn}
                  className="px-6 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {checkingIn ? (
                    <>
                      <svg className="inline w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Checking In...
                    </>
                  ) : (
                    'Check In Child'
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
